/**
 * Pure helpers for "streaming dictation" into a plain textarea.
 *
 * AssemblyAI emits Turn events twice: as `end_of_turn=false` partials
 * while the user is still speaking, and once with `end_of_turn=true`
 * when it's confident the utterance has ended. Default behaviour waits
 * for the final event before showing text — which feels slow when a
 * doctor speaks fluently without pauses.
 *
 * Instead we want to write each partial directly into the textarea and
 * replace it in place as AssemblyAI refines its hypothesis. These
 * helpers do the string maths for that, decoupled from React state.
 */

/**
 * Replace the active partial-range inside `current` with `partial`,
 * returning the new string and the new length of the partial-range so
 * the caller can persist it for the next replacement.
 *
 * `start` is the offset where the partial range begins in `current`.
 * `oldLength` is the length of the previous partial (0 for the first
 * partial of a turn).
 */
export function applyPartialReplacement(args: {
  current: string;
  start: number;
  oldLength: number;
  partial: string;
}): { text: string; newLength: number } {
  const before = args.current.substring(0, args.start);
  const after = args.current.substring(args.start + args.oldLength);
  return {
    text: before + args.partial + after,
    newLength: args.partial.length,
  };
}

/**
 * Decide whether the partial range is still safe to mutate. If the
 * substring at [start, start+oldLength) no longer matches the partial
 * we last wrote, the user has typed/edited into our range — abandon it
 * to avoid clobbering their edits.
 */
export function isPartialRangeIntact(args: {
  current: string;
  start: number;
  oldLength: number;
  lastPartial: string;
}): boolean {
  if (args.start < 0) return false;
  if (args.start + args.oldLength > args.current.length) return false;
  const slice = args.current.substring(args.start, args.start + args.oldLength);
  return slice === args.lastPartial;
}
